import { useCallback, useEffect, useState } from "react";
import { ArchiveIndex } from "../components/ArchiveIndex";
import { LoadingState } from "../components/LoadingState";
import { UnavailableState } from "../components/UnavailableState";
import { useFtmDataService } from "../context/FtmDataServiceContext";
import type { ArchiveEntry } from "../types";
import type { PageInfo } from "../publication/types";

export function ArchivePage() {
  const service = useFtmDataService();
  const [search, setSearch] = useState("");
  const [data, setData] = useState<ArchiveEntry[] | null>(null);
  const [page, setPage] = useState<PageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    setPage(null);
    const request = { search: search || undefined, limit: 20 };
    const load = service.listArchivePage
      ? service.listArchivePage(request)
      : service.listArchive({ search }).then((items) => ({ items, page: { limit: items.length, nextCursor: null, hasMore: false } }));
    load.then((result) => {
      if (!active) return;
      setData(result.items);
      setPage(result.page);
      setLoading(false);
    }).catch((reason: unknown) => {
      if (!active) return;
      setError(reason instanceof Error ? reason.message : "The archive provider is unavailable.");
      setLoading(false);
    });
    return () => { active = false; };
  }, [search, service]);

  const loadMore = useCallback(async () => {
    if (!page?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const request = { search: search || undefined, cursor: page.nextCursor, limit: page.limit };
      const result = service.listArchivePage
        ? await service.listArchivePage(request)
        : { items: [] as ArchiveEntry[], page: { ...page, nextCursor: null, hasMore: false } };
      setData((current) => [...(current ?? []), ...result.items]);
      setPage(result.page);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "The next archive page is unavailable.");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, page, search, service]);

  if (loading) return <LoadingState label="Opening the report archive." />;
  if (error || !data) return <UnavailableState title="The Report Archive could not load." message={error ?? "The archive provider is unavailable."} />;

  return <main id="main-content" className="site-shell page-space archive-page"><div className="page-heading-row"><div><p className="eyebrow">FTM / RESEARCH ARCHIVE</p><h1 className="page-title page-title-compact">Report Archive</h1><p className="page-intro">A chronological research index: what the market showed, what FTM estimated, and how the view was recorded.</p></div><div className="archive-count"><strong>{data.length}</strong><span>entries</span></div></div><label className="search-field"><span>Search reports</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Title, category, platform" /></label><ArchiveIndex entries={data} />{page?.hasMore ? <button type="button" className="button-secondary" onClick={loadMore} disabled={loadingMore}>{loadingMore ? "Loading…" : "Load more"}</button> : null}</main>;
}
